import { useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { useSelector, useDispatch } from "react-redux";

import Header from "./components/Header";
import Login from "./screens/Login";
import Profile from "./screens/Profile";
import User from "./screens/Users";
import Home from "./screens/Home";
import Last12MonthsDashboard from "./screens/Last12MonthsDashboard";
import ServiceHoursAllStatesLast4Years from "./components/ServiceHoursAllStatesLast4Years";
import AverageResolutionTime from "./components/AverageResolutionTime";
import AverageResponseTime from "./components/AverageResponseTime";
import ProtectedRoute from "./routing/ProtectedRoute";
import Customers from "./screens/Customers";
import CustomerContact from "./screens/Customers/CustomerContact";
import Reports from "./screens/Reports";
import Sidebar from "./components/Sidebar";
import AllServiceTickets from "./screens/AllServiceTickets";
import MachineModel from "./screens/MachineModel";
import AddEditMachineModel from "./screens/MachineModel/AddEditMachineModel";
import SalesCalls from "./screens/SalesCalls";
import AddEditNewTemplates from "./screens/Templates/AddEditNewTemplates";
import NotFoundPage from "./screens/NotFound";
import LoadingIndicator from "./screens/LoadingPage";
import CustomerRatings from "./screens/Dashboards/CustomerRatings";

import { readAsyncStorageValues } from "./features/common/actions";

import { ToastContainer } from "react-toastify";
import SalesCallDetailView from "./screens/SalesCalls/SalesCallDetailView";
import AllSvcTicketForLastXDays from "./screens/AllSvcTicketForLastXDays";
import MyTickets from "./screens/MyTickets";
import Templates from "./screens/Templates";
import ViewServiceTicket from "./screens/AllServiceTickets/ViewServiceTicket";
import CreateServiceTicket from "./screens/AllServiceTickets/CreateServiceTicket";
import ViewReport from "./screens/AllServiceTickets/ViewReport";
import Notify from "./screens/Notify";
import PaidTickets from "./screens/PaidTickets";
import ResponseResolutionDashboard from "./screens/ResponseResolutionDashboard";

import { getClientName } from "./features/common/actions";
import danaireFavicon from "./app/assets/images/danaire/favicon.png";
import clarityFavicon from "./app/assets/images/clarity/favicon.png";
import agelessFavicon from "./app/assets/images/agelessind/favicon.png";
import clarityDevFavicon from "./app/assets/images/clarity_dev/favicon.png";
import froidFavicon from "./app/assets/images/froid/favicon.png";
// import "./App.css";

const FAVICONS = {
  clarity_dev: clarityDevFavicon,
  clarity: clarityFavicon,
  danaire: danaireFavicon,
  agelessind: agelessFavicon,
  froid: froidFavicon,
};

function App() {
  const dispatch = useDispatch();
  const clientName = useSelector((state) => state.common.clientName);

  const theme = createTheme({
    typography: {
      fontFamily: ["Karla"],
    },
    palette: {
      primary: {
        main: "#5D8AA8",
      },
      secondary: {
        main: "#DAA520",
      },
    },
  });
  useEffect(() => {
    dispatch(getClientName());
  }, [dispatch]);
  
  useEffect(() => {
    if (clientName) {
      // console.log("----clientName:", clientName)
      document.title = clientName.toUpperCase();

      const link =
        document.querySelector("link[rel~='icon']") ||
        document.createElement("link");
      link.rel = "icon";
      link.href = FAVICONS[clientName] || clarityFavicon;
      document.getElementsByTagName("head")[0].appendChild(link);
    }
  }, [clientName]);

  function MainContent() {
    const dispatch = useDispatch();
    const { userInfo, userCurrentRole } = useSelector((state) => state.auth);
    const { asyncStorageValues, loading } = useSelector((state) => state.common);
    useEffect(() => {
      if (userInfo && Object.keys(userInfo).length) dispatch(readAsyncStorageValues());
    }, [dispatch, userInfo]);

    const location = useLocation();
    const mainStyles = { paddingLeft: location.pathname !== "/login" ? "230px" : "0" };

    return (
      <main style={mainStyles}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route element={<ProtectedRoute />}>
            {userCurrentRole === asyncStorageValues?.allTypes?.USER_ROLES?.ROLE_REGIONAL_SALES_MANAGER && (
              <>
                <Route path="/" element={<Home />} />
                <Route path="/dashboard/last-12-months" element={<Last12MonthsDashboard />} />
                <Route path="/dashboard/last-4-years" element={<ServiceHoursAllStatesLast4Years />} />
                <Route path="/dashboard/response-resolution" element={<ResponseResolutionDashboard />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/users" element={<User />} />
                <Route path="/reports" element={<Reports />} />
                <Route path="/customers" element={<Customers />} />
                <Route path="/customer-contact/:id" element={<CustomerContact />} />
                <Route path="/AllServiceTickets" element={<AllServiceTickets />} />
                <Route path="/AllSvcTicketsForLastXdays" element={<AllSvcTicketForLastXDays />} />
                <Route path={"/view-report/:serviceTicketId"} element={<ViewReport />} />
              </>
            )}

            {userCurrentRole === asyncStorageValues?.allTypes?.USER_ROLES?.ROLE_NATIONAL_SERVICE_MANAGER && (
              <>
                <Route path="/" element={<Home />} />
                <Route path="/dashboard/last-12-months" element={<Last12MonthsDashboard />} />
                <Route path="/dashboard/last-4-years" element={<ServiceHoursAllStatesLast4Years />} />
                <Route path="/dashboard/response-resolution" element={<ResponseResolutionDashboard />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/users" element={<User />} />
                <Route path="/reports" element={<Reports />} />
                <Route path="/AllServiceTickets" element={<AllServiceTickets />} />
                <Route path="/AllSvcTicketsForLastXdays" element={<AllSvcTicketForLastXDays />} />
                <Route path={"/view-report/:serviceTicketId"} element={<ViewReport />} />
              </>
            )}
            {userCurrentRole === asyncStorageValues?.allTypes?.USER_ROLES?.ROLE_SERVICE_COORDINATOR && (
              <>
                <Route path="/" element={<Home />} />
                <Route path="/dashboard/last-12-months" element={<Last12MonthsDashboard />} />
                <Route path="/dashboard/response-resolution" element={<ResponseResolutionDashboard />} />
                <Route path="/dashboard/last-4-years" element={<ServiceHoursAllStatesLast4Years />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/users" element={<User />} />
                <Route path="/reports" element={<Reports />} />
                <Route path="/AllServiceTickets" element={<AllServiceTickets />} />
                <Route path="/AllSvcTicketsForLastXdays" element={<AllSvcTicketForLastXDays />} />
                <Route path={"/all-service-ticket-view/:serviceTicketId"} element={<ViewServiceTicket />} />
                <Route path={"/add-service-ticket"} element={<CreateServiceTicket />} />
                <Route path={"/edit-service-ticket" + "/:id"} element={<AddEditMachineModel />} />
                <Route path={"/MyTickets"} element={<MyTickets />} />
                <Route path={"/view-report/:serviceTicketId"} element={<ViewReport />} />
                <Route path={"/edit-service-ticket" + "/:id"} element={<CreateServiceTicket />} />
                <Route path={"/notify"} element={<Notify />} />
                <Route path={"/PaidTickets"} element={<PaidTickets />} />
              </>
            )}

            {(userCurrentRole === asyncStorageValues?.allTypes?.USER_ROLES?.ROLE_DATA_OWNER ||
  userCurrentRole === asyncStorageValues?.allTypes?.USER_ROLES?.ROLE_SERVICE_COORDINATOR) && (
              <>
                <Route path="/" element={<Home />} />
                <Route path="/dashboard/last-12-months" element={<Last12MonthsDashboard />} />
                <Route path="/dashboard/last-4-years" element={<ServiceHoursAllStatesLast4Years />} />
                <Route path="/dashboard/response-resolution" element={<ResponseResolutionDashboard />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/users" element={<User />} />
                <Route path="/reports" element={<Reports />} />
                <Route path="/customers" element={<Customers />} />
                <Route path="/customer-contact/:id" element={<CustomerContact />} />
                <Route path="/AllServiceTickets" element={<AllServiceTickets />} />
                <Route path="/AllSvcTicketsForLastXdays" element={<AllSvcTicketForLastXDays />} />
                <Route path="/machine-model" element={<MachineModel />} />
                <Route path={"/add-machine-model"} element={<AddEditMachineModel />} />
                <Route path={"/edit-machine-model" + "/:id"} element={<AddEditMachineModel />} />
                <Route path="/templates" element={<Templates />} />
                <Route path={"/add-template"} element={<AddEditNewTemplates />} />
                <Route path={"/edit-template" + "/:id"} element={<AddEditNewTemplates />} />
              </>
            )}
            {(userCurrentRole === asyncStorageValues?.allTypes?.USER_ROLES?.ROLE_DATA_OWNER) ||
  userCurrentRole === asyncStorageValues?.allTypes?.USER_ROLES?.ROLE_SERVICE_COORDINATOR &&
              asyncStorageValues?.configData?.dataOwnerPermissions?.create_edit_tickets && (
                <>
                  <Route path={"/add-service-ticket"} element={<CreateServiceTicket />} />
                  <Route path={"/edit-service-ticket" + "/:id"} element={<CreateServiceTicket />} />
                </>
              )}
            {(userCurrentRole === asyncStorageValues?.allTypes?.USER_ROLES?.ROLE_DATA_OWNER ||
  userCurrentRole === asyncStorageValues?.allTypes?.USER_ROLES?.ROLE_SERVICE_COORDINATOR)&&
              asyncStorageValues?.configData?.dataOwnerPermissions?.create_edit_tickets && (
                <>
                  <Route path={"/add-service-ticket"} element={<CreateServiceTicket />} />
                  <Route path={"/edit-service-ticket" + "/:id"} element={<CreateServiceTicket />} />
                </>
              )}
            <Route path={"/all-service-ticket-view/:serviceTicketId"} element={<ViewServiceTicket />} />
            <Route path={"/view-report/:serviceTicketId"} element={<ViewReport />} />
            <Route path="/customer-ratings" element={<CustomerRatings />} />
            <Route path="/sales-calls" element={<SalesCalls />} />
            <Route path="/sales-calls-details" element={<SalesCallDetailView />} />
          </Route>
          <Route path="*" element={loading ? <LoadingIndicator /> : <NotFoundPage />} />
        </Routes>
      </main>
    );
  }
  const drawerWidth = 240;

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Sidebar drawerWidth={drawerWidth} />
        <Header />
        <MainContent />
        <ToastContainer />
      </Router>
    </ThemeProvider>
  );
}

export default App;
